import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { HouseIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { To } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { GetListingPreviewQuery } from '@graphql/__generated__/graphql';
import {
  ListingCardSnippet,
  ListingCardSnippetSkeletton,
} from '@modules/_shared/components/ListingCardSnippet';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useMlsListing } from '@modules/mls/mlsListingContext';
import { useLocale } from '@shared/hooks/locale';

const GET_LISTING_PREVIEW_QUERY = gql(/* GraphQL */ `
  query GetListingPreview(
    $listingId: uuid!
    $includeOffer: Boolean!
    $offerId: uuid
  ) {
    lots_by_pk(id: $listingId) {
      id
      broker_id
      ...ListingCardSnippet
      mandate_type
      last_accepted_offer: offers(where: { id: { _eq: $offerId } })
        @include(if: $includeOffer) {
        id
        amount
        buyer_id
      }
    }
  }
`);

export type ListingPreview = NonNullable<
  GetListingPreviewQuery['lots_by_pk']
> | null;

type RaListingPreviewProps = {
  srcListingFieldName?: string;
  targetListingFieldName?: string;
  listingLink?: (listingId: string) => To;
  onListingFetched?: (listing: ListingPreview) => void;
  preventFormDataUpdate?: boolean;
  orientation?: 'horizontal' | 'vertical';
  includeOfferInQuery?: string;
};

export type RaListingPreviewData<
  TSrcFieldName extends string = 'listing_id',
  TTargetFieldName extends string = '__listing',
> = {
  [Property in TSrcFieldName | TTargetFieldName]: Property extends TSrcFieldName
    ? string | null
    : ListingPreview;
};

const MlsListingPreviewCard: React.FC = () => {
  const { t } = useLocale();
  const mlsCtx = useMlsListing();

  if (mlsCtx?.lot == null) {
    return null;
  }

  const { lot } = mlsCtx;
  const property = lot.property;

  if (property == null) {
    return null;
  }

  const imageUrl = property.property_images[0]?.image.url;
  const address = formatAddress(
    {
      route: property.route ?? undefined,
      street_number: property.street_number ?? undefined,
      postcode: property.postcode ?? undefined,
      locality: property.locality ?? undefined,
      state: property.state ?? undefined,
    },
    ', ',
  );
  const currency = lot.currency ?? 'CHF';
  const priceLabel =
    lot.offer_type === 'rent'
      ? (() => {
          const rent = (lot.rent_net ?? 0) + (lot.rent_extra ?? 0);
          return rent > 0 ? `${rent.toLocaleString()} ${currency} / mo` : null;
        })()
      : lot.sale_price != null && lot.sale_price > 0
      ? `${lot.sale_price.toLocaleString()} ${currency}`
      : null;
  const brokerName =
    lot.broker?.full_name ??
    [lot.broker?.first_name, lot.broker?.last_name].filter(Boolean).join(' ');

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'row',
        minHeight: 140,
        alignItems: 'stretch',
      }}
    >
      <CardMedia sx={{ width: 150, minWidth: 120 }}>
        {imageUrl != null ? (
          <Image
            src={imageUrl}
            options={{ w: 150, h: 140, f: 'jpg', c: 'fill' }}
          />
        ) : (
          <Box sx={{ color: 'grey.400', margin: 8 }}>
            <HouseIcon size={32} />
          </Box>
        )}
      </CardMedia>
      <CardContent
        sx={{
          flexGrow: 1,
          '&.MuiCardContent-root:last-child': { pb: 2 },
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            height: '100%',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <Stack
              direction="column"
              sx={{
                flexGrow: 1,
              }}
            >
              <Typography variant="caption" noWrap>
                {address}
              </Typography>
              <Typography variant="body1" noWrap>
                {lot.title != null && lot.title !== '' ? lot.title : '-'}
              </Typography>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                }}
              >
                {priceLabel ?? t('On request')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                }}
              >
                -
              </Typography>
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.5 }} orientation="vertical" />

          <Stack direction="column" spacing={1} sx={{ maxWidth: 350 }}>
            {brokerName.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption">{t('Agent')}</Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    src={lot.broker?.profile_image_url ?? undefined}
                    sx={{ width: 24, height: 24, fontSize: 12 }}
                  >
                    {brokerName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">{brokerName}</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const RaListingPreview: React.FC<RaListingPreviewProps> = ({
  listingLink,
  onListingFetched,
  srcListingFieldName = 'listing_id',
  targetListingFieldName = '__listing',
  preventFormDataUpdate = false,
  orientation = 'horizontal',
  includeOfferInQuery,
}) => {
  const { setValue } = useFormContext();
  const listingId = useWatch({ name: srcListingFieldName });
  const listingRef = useRef<ListingPreview>(null);
  const mlsCtx = useMlsListing();
  const isMlsLot =
    mlsCtx?.lot != null && listingId != null && mlsCtx.lot.id === listingId;

  const { data, loading } = useQuery(GET_LISTING_PREVIEW_QUERY, {
    variables: {
      listingId,
      offerId: includeOfferInQuery,
      includeOffer: includeOfferInQuery != null,
    },
    skip: listingId == null,
  });

  useEffect(() => {
    if (data?.lots_by_pk?.id !== listingRef.current?.id) {
      onListingFetched?.(data?.lots_by_pk ?? null);
      listingRef.current = data?.lots_by_pk ?? null;

      if (!preventFormDataUpdate) {
        setValue(targetListingFieldName, data?.lots_by_pk ?? null);
      }
    }
  }, [
    data?.lots_by_pk,
    onListingFetched,
    setValue,
    targetListingFieldName,
    preventFormDataUpdate,
  ]);

  return (
    <>
      {data?.lots_by_pk != null && (
        <ListingCardSnippet
          listing={data.lots_by_pk}
          link={listingLink?.(data.lots_by_pk.id)}
          orientation={orientation}
        />
      )}
      {data?.lots_by_pk == null && listingId != null && !loading && (
        <>
          {isMlsLot ? (
            <MlsListingPreviewCard />
          ) : (
            <ListingCardSnippetSkeletton
              orientation={orientation}
              restrictedAccess={true}
            />
          )}
        </>
      )}
      {loading && <ListingCardSnippetSkeletton orientation={orientation} />}
    </>
  );
};
